import React, { createContext, useContext, useMemo, useState } from "react";
import "./ChatHistory.module.css";
import { ChatSender } from "../../common/constants";
import { DEFAULT_TEXT, getUserConfigText } from "../../config";
import { useEffect } from "react";
import { scrollHistoryToBottom } from "../Widget/utils";
import { useConfig } from "../Widget/ConfigContext";
import { calculateVoice, getSynthesisKey } from "../../common/langUtils";
import { MessageBubble } from "./MessageBubble";
import { BubbleWrapper } from "./BubbleWrapper";

const HistoryContext = createContext({
  synthKey: undefined,
  doSynth: () => {},
});

export function useHistoryContext() {
  return useContext(HistoryContext);
}

export function replaceWithLink(node, src) {
  const replacement = document.createElement("a");
  replacement.setAttribute("href", src);
  replacement.setAttribute("target", "_blank");
  replacement.classList.add("widget-link");
  replacement.appendChild(document.createTextNode(src));
  node.replaceWith(replacement);
}

// all markdown or html tags allowed to appear in messages
export const allowedTags = [
  "a",
  "button",
  "em",
  "strong",
  "img",
  "audio",
  "video",
];

/**
 * @param {Node} node
 */
export function getSourceFromNode(node) {
  let src = null;

  // if the src property is on the parent node, return it
  if ((src = node.getAttribute("src"))) {
    return src;
  } else {
    Array.from(node.childNodes).forEach((child) => {
      if (src === null && child.tagName.toLowerCase() === "source") {
        src = child.getAttribute("src");
      }
    });
    return src;
  }
}

export function SystemMessage({ text }) {
  const { config, colors } = useConfig();
  return (
    <p
      style={{
        paddingLeft: "0.5rem",
        paddingRight: "0.5rem",
        fontFamily: "sans-serif",
        fontSize: config.systemLabelFontSize,
        fontWeight: config.systemLabelFontWeight,
        color: colors.systemLabelText,
        textAlign: "center",
        width: "100%",
      }}
    >
      {text}
    </p>
  );
}

function TypingIndicator() {
  const { colors } = useConfig();
  const textColor = colors.botMessageText;

  const [dotCount, setDotCount] = useState(1);
  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prevDotCount) => {
        return prevDotCount === 5 ? 1 : prevDotCount + 1;
      });
    }, 400);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const dots = Array(dotCount).fill(".");

  return <span style={{ color: textColor }}>{dots}</span>;
}

/**
 * @typedef {import('../../types/config').ConfigObject} ConfigObject
 */
/**
 *
 * @param {{
 *   messages: any,
 *   thinking: any,
 *   minimal: any,
 *   toggleChatOpen: any,
 *   hideChat: any
 * }} param0
 * @returns
 */
export default function ChatHistory({
  messages,
  thinking,
  minimal,
  toggleChatOpen,
  hideChat,
}) {
  const { config } = useConfig();

  const histDiscText = useMemo(
    () =>
      getUserConfigText(
        config,
        "historyDisclaimer",
        DEFAULT_TEXT.en.historyDisclaimer,
      ),
    [document.cookie],
  );

  // setup speechSynthesis
  const [synthKey, setSynthKey] = useState(undefined);
  const [voice, setVoice] = useState(undefined);

  function updateSynthesisKey() {
    const synthLangKey = getSynthesisKey(config);

    if (synthLangKey === false) {
      setSynthKey(undefined);
    } else {
      setSynthKey(synthLangKey);
    }
  }

  function doSynth(message, onEnd) {
    if (synthKey) {
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = synthKey;

      // if for some reason we couldn't pick a voice, default to the system's
      // voice for the given language
      if (voice) {
        utterance.voice = voice;
      }

      if (onEnd) {
        utterance.onend = onEnd;
      }

      speechSynthesis.speak(utterance);
    }
  }

  // when the synthesis key changes, calculate the new best voice from the external
  // list
  useEffect(() => {
    if (synthKey) {
      (async () => {
        const calculatedVoice = await calculateVoice(synthKey);
        setVoice(calculatedVoice ?? undefined);
      })();
    }
  }, [synthKey]);

  // update synthesis once when voices are loaded
  useEffect(() => {
    // only set voice synthesis if tts buttons are enabled
    if (config.showTtsButtons) {
      speechSynthesis.onvoiceschanged = () => {
        updateSynthesisKey();

        speechSynthesis.onvoiceschanged = undefined;
      };
    }
  }, []);

  useEffect(() => {
    if (config.showTtsButtons) {
      updateSynthesisKey();
    }
  }, [document.cookie, config?.overrideLanguage]);

  useEffect(() => {
    scrollHistoryToBottom();
  }, [messages, thinking]);

  const onCrossClick = () => {
    if (hideChat) {
      hideChat();
    }
  };

  return (
    <HistoryContext.Provider value={{ synthKey, doSynth }}>
      <div
        className={
          "magicform-chat-history-minimal " +
          (minimal
            ? "magicform-chat-history-hide-scrollbar"
            : "magicform-chat-history-show-scrollbar")
        }
      >
        {histDiscText && (
          <p
            dir="auto"
            style={{
              paddingLeft: "0.5rem",
              paddingRight: "0.5rem",
              fontFamily: "sans-serif",
              fontSize: config.historyDiscFontSize,
              fontWeight: config.historyDiscFontWeight,
              color: "gray",
              textAlign: "center",
              width: "100%",
            }}
          >
            {histDiscText}
          </p>
        )}
        {messages.length && minimal ? (
          <div onClick={onCrossClick} className="magicform-delete-button">
            &times;
          </div>
        ) : (
          <></>
        )}
        {messages.map((message, index) => (
          <MessageBubble
            key={index}
            message={message.text}
            sender={message.sender}
            minimal={minimal}
            toggleChatOpen={toggleChatOpen}
          />
        ))}
        {thinking && (
          <BubbleWrapper
            minimal={minimal}
            sender={ChatSender.bot}
            showTts={false}
          >
            <TypingIndicator />
          </BubbleWrapper>
        )}
        <div className="magicform-scroll-anchor"></div>
      </div>
    </HistoryContext.Provider>
  );
}
